import React from 'react'
import { createRoot, type Root } from 'react-dom/client'

import { registerBuiltInEditors } from './editors/registerBuiltIns'
import { ReactGrid } from './ReactGrid'
import type { GridConfig, GridEndpoints } from './types'

const roots = new WeakMap<HTMLElement, Root>()
const lastProps = new WeakMap<HTMLElement, string>()

const parseJson = <T>(raw: string | null | undefined, fallback: T): T => {
	if (raw === null || raw === undefined || raw === '') {
		return fallback
	}
	try {
		return JSON.parse(raw) as T
	} catch {
		return fallback
	}
}

const ensureRoot = (el: HTMLElement): Root => {
	let root = roots.get(el)
	if (root === undefined) {
		root = createRoot(el)
		roots.set(el, root)
	}
	return root
}

export const bootstrapReactGrid = (scope: ParentNode = document): void => {
	registerBuiltInEditors()
	scope.querySelectorAll<HTMLElement>('[data-react-component="react-grid"]').forEach((el) => {
		// handleAjaxComplete re-runs bootstrap after every naja AJAX; without this guard,
		// each call hands ReactGrid fresh config/endpoints object refs that bust the
		// `api` useMemo in useGridData and drive an infinite fetch loop.
		const configRaw = el.dataset.config ?? ''
		const endpointsRaw = el.dataset.endpoints ?? ''
		const propsKey = `${configRaw}|${endpointsRaw}`
		if (lastProps.get(el) === propsKey) {
			return
		}

		const config = parseJson<GridConfig | null>(configRaw, null)
		const endpoints = parseJson<GridEndpoints | null>(endpointsRaw, null)
		if (config === null || endpoints === null) {
			return
		}
		lastProps.set(el, propsKey)
		ensureRoot(el).render(React.createElement(ReactGrid, { config, endpoints }))
	})
}
